import { useContext, useEffect } from "react"

import { CardCommercialOffersProgram } from "./ui"
import { FieldsErrorType, ICardCommercialOffersProgram, ICardCommercialOffersProgramContainer, IFieldsError } from "./types"

import { useValue } from "@hooks/useValue"
import { useFieldsErrorAnimation } from "@hooks/useFieldsErrorAnimation"
import { validFormData } from "@hooks/useForm"

import { DictNameType } from "@smart/dictSelector/types"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"



/** Контейнер - Анкета программы по комерческому предложению */
export const CardCommercialOffersProgramContainer = ({isOpen, cardMode, dbName, cardData, handleButtonOk, handleButtonCancel}: ICardCommercialOffersProgramContainer) => {
    const notify = useContext(NotificationContext)

    const fieldsError = useValue<IFieldsError>({})

    const txtProgramName = useValue<string>('')
    const txtProgramNameActual = useValue<string>('')
    const txtAttestationTypeID = useValue<number | ''>('')
    const txtAttestationTypeName = useValue<string>('')
    const txtDocumentTypeID = useValue<number | ''>('')
    const txtDocumentTypeName = useValue<string>('')
    
    const txtVolumeHours = useValue<number | ''>('')
    const txtDurationEducation = useValue<string>('')
    const txtPrice = useValue<number | ''>('')
    const txtListenersCount = useValue<number | ''>('')

    //======================================================================================

    useEffect(() => {
        if (isOpen) {
            cardMode === 'new' && txtListenersCount.setValue(1)

            if (cardMode === 'edit') {
                txtProgramName.setValue(cardData?.programName || '')
                txtProgramNameActual.setValue(cardData?.programNameActual || '')
                txtAttestationTypeID.setValue(cardData?.attestationTypeID || '')
                txtAttestationTypeName.setValue(cardData?.attestationTypeName || '')
                txtDocumentTypeID.setValue(cardData?.documentTypeID || '')
                txtDocumentTypeName.setValue(cardData?.documentTypeName || '')
                
                txtVolumeHours.setValue(cardData?.volumeHours || '')
                txtDurationEducation.setValue(cardData?.durationEducation || '')
                txtPrice.setValue(cardData?.price || '')
                txtListenersCount.setValue(cardData?.listenersCount || '')
            }
        }

        !isOpen && clearData()
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    //======================================================================================

    /** Очистка формы */
    const clearData = () => {
        txtProgramName.setValue('')
        txtProgramNameActual.setValue('')
        txtAttestationTypeID.setValue('')
        txtAttestationTypeName.setValue('')
        txtDocumentTypeID.setValue('')
        txtDocumentTypeName.setValue('')
        
        txtVolumeHours.setValue('')
        txtDurationEducation.setValue('')
        txtPrice.setValue('')
        txtListenersCount.setValue('')
    }

    /** При изменении поля продолжительность, меняем запятую на точку или убираем пробел */
    const txtDurationEducationOnChange = (value: string) => {
        txtDurationEducation.setValue(value.replace(',', '.').replace(' ', ''))
    }

    /** При выборе данных из справочника */
    const dictSelectorData = (dictName: DictNameType, data: {id: number, name: string, param1?: string, param2?: string, param3?: string, param4?: string, param5?: string, param6?: string}) => {
        switch (dictName) {
            case 'coursesContract': {
                txtProgramName.setValue(data.name)
                txtProgramNameActual.setValue(data.name)
                break
            }

            case 'documentTypes': {
                txtDocumentTypeID.setValue(Number(data.id))
                txtDocumentTypeName.setValue(data.name)
                break
            }

            case 'attestationType': {
                txtAttestationTypeID.setValue(Number(data.id))
                txtAttestationTypeName.setValue(data.name)
                break
            }
        }
    }


    
    /** Кнопка сохранить все данные */
    const handleBtnSave = () => {
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'programName', value: txtProgramName.value.toString(), validType: 'notEmpty', isRequired: true, errorMessage: 'Наименование программы не выбрано.'},
            {fieldName: 'programNameActual', value: txtProgramNameActual.value.toString(), validType: 'notEmpty', isRequired: true, errorMessage: 'Наименование программы фактическое, должно быть заполнено.'},
            
            {fieldName: 'documentTypeName', value: txtDocumentTypeID.value.toString(), validType: 'notEmpty', isRequired: true, errorMessage: 'Тип программы не выбран.'},
            {fieldName: 'attestationTypeName', value: txtAttestationTypeID.value.toString(), validType: 'notEmpty', isRequired: true, errorMessage: 'Тип аттестации не выбран.'},
            
            {fieldName: 'durationEducation', value: txtDurationEducation.value.toString(), validType: 'numberDecimal', isRequired: true, errorMessage: 'Продолжительность не корректное значение.'},
            {fieldName: 'volumeHours', value: txtVolumeHours.value.toString(), validType: 'number_1-9', isRequired: true, errorMessage: 'Объём не корректное значение.'},
            {fieldName: 'price', value: txtPrice.value.toString(), validType: 'number_1-9', isRequired: true, errorMessage: 'Сумма не корректное значение.'},
            {fieldName: 'listenersCount', value: txtListenersCount.value.toString(), validType: 'number_1-9', isRequired: true, errorMessage: 'Кол. слушателей не корректное значение.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnimation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }

        if (validateData.length === 0) { // Сохраняем данные
            handleButtonOk && handleButtonOk({
                commercialOfferProgramID: Number(cardData?.commercialOfferProgramID),

                programName: txtProgramName.value,
                programNameActual: txtProgramNameActual.value,
                
                attestationTypeID: Number(txtAttestationTypeID.value),
                attestationTypeName: txtAttestationTypeName.value,
            
                documentTypeID: Number(txtDocumentTypeID.value),
                documentTypeName: txtDocumentTypeName.value,
            
                volumeHours: Number(txtVolumeHours.value),
                durationEducation: txtDurationEducation.value,
                price: Number(txtPrice.value),
            
                listenersCount: Number(txtListenersCount.value)
            })
        //     loadingTitle.setValue(cardMode === 'new' ? 'Добавление программы...' : 'Сохранение данных...')
        }
    }
    /** Активация анимации ошибки в указанных полях */
    const fieldsErrorAnimation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)

    //======================================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardCommercialOffersProgram = {
        isOpen, cardMode, fieldsError, dbName: dbName || '',

        txtProgramName,
        txtProgramNameActual,
        txtAttestationTypeID,
        txtAttestationTypeName,
        txtDocumentTypeID,
        txtDocumentTypeName,
        
        txtVolumeHours,
        txtDurationEducation,
        txtPrice,
        txtListenersCount,

        txtDurationEducationOnChange, dictSelectorData, 
        handleBtnSave, handleButtonCancel
    }

    return <>
        <CardCommercialOffersProgram {...propsToComponent} />
    </>
}